import { useQuery, useMutation } from '@tanstack/react-query';
import { useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { SEARCH_DEBOUNCE_MS } from '../constants';
import type {
  CaseType,
  Stage,
  FieldDef,
  FieldOption,
  FilterPayload,
  FilterResponse,
  GlobalSearchResult,
} from '../types/cases';

/* ─────────────────────────────────────────────
 * 1. User permissions (allowed case types)
 * ───────────────────────────────────────────── */

export function useUserPermissions() {
  return useQuery<string[] | null>({
    queryKey: ['userPermissions'],
    queryFn: async () => {
      const { data } = await api.get('/api/users/permissions');
      if (data.success && Array.isArray(data.allowed_case_types)) {
        return data.allowed_case_types as string[];
      }
      return null; // null = no restrictions
    },
    staleTime: 5 * 60_000,
  });
}

/* ─────────────────────────────────────────────
 * 2. Case types for filter dropdown
 * ───────────────────────────────────────────── */

export function useCaseTypes() {
  return useQuery<CaseType[]>({
    queryKey: ['caseTypes'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/case-types');
      if (data.success && data.data) return data.data as CaseType[];
      return [];
    },
    staleTime: 5 * 60_000,
  });
}

/* ─────────────────────────────────────────────
 * 3. Stages for a selected case type
 * ───────────────────────────────────────────── */

export function useStages(caseTypeName: string | null) {
  return useQuery<Stage[]>({
    queryKey: ['stages', caseTypeName],
    queryFn: async () => {
      const { data } = await api.get(`/api/stages/case-type/${encodeURIComponent(caseTypeName!)}`);
      if (data.success && data.stages) {
        return (data.stages as Stage[])
          .filter((s) => s.is_active !== false)
          .sort((a, b) => (a.stage_order ?? 0) - (b.stage_order ?? 0));
      }
      return [];
    },
    enabled: !!caseTypeName,
  });
}

/* ─────────────────────────────────────────────
 * 4. Field definitions for a case type (by ID)
 * ───────────────────────────────────────────── */

export function useFieldDefs(caseTypeId: number | null) {
  return useQuery<FieldDef[]>({
    queryKey: ['fieldDefs', caseTypeId],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/fields');
      if (!data.success || !data.data) return [];
      const fields = data.data as FieldDef[];
      // Filter to fields that belong to the selected case type
      return fields.filter((f) => {
        if (!f.case_types || !Array.isArray(f.case_types)) return false;
        return f.case_types.some((ctId) => Number(ctId) === caseTypeId);
      });
    },
    enabled: caseTypeId != null,
  });
}

/* ─────────────────────────────────────────────
 * 5. Field dropdown options
 * ───────────────────────────────────────────── */

export function useFieldOptions(fieldId: number | null) {
  return useQuery<FieldOption[]>({
    queryKey: ['fieldOptions', fieldId],
    queryFn: async () => {
      const { data } = await api.get(`/api/fieldconfig/fields/${fieldId}/options`);
      if (data.success && data.data) return data.data as FieldOption[];
      return [];
    },
    enabled: fieldId != null,
  });
}

/* ─────────────────────────────────────────────
 * 6. Run filter query (mutation for imperative calls)
 * ───────────────────────────────────────────── */

export function useFilterCases() {
  return useMutation<{ rows: Record<string, unknown>[]; total: number }, Error, FilterPayload>({
    mutationFn: async (payload) => {
      const { data } = await api.post<FilterResponse>('/api/cases/filter', payload);
      const rows = data.cases?.rows ?? [];
      const total = data.cases?.total ?? data.total ?? rows.length;
      return { rows, total };
    },
  });
}

/* ─────────────────────────────────────────────
 * 7. Export all cases (chunked fetch)
 * ───────────────────────────────────────────── */

export function useExportCases() {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const formatExportValue = useCallback(
    (column: string, value: unknown, dateColumns?: Set<string>) => {
      if (value == null) return '';

      const rawValue = String(value);
      const isDateColumn =
        dateColumns?.has(column) ||
        /(?:^|_)(?:created|updated|date|datetime|timestamp|time)(?:$|_)/i.test(column);

      if (isDateColumn) {
        const parsed = new Date(rawValue);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleDateString('en-GB');
        }
      }

      return rawValue;
    },
    [],
  );

  const exportCases = useCallback(
    async (
      basePayload: FilterPayload,
      totalCases: number,
      toast?: (
        msg: string,
        type?: 'notify' | 'success' | 'error' | 'warning' | 'info',
        dur?: number,
      ) => void,
      options?: { dateColumns?: string[] },
    ) => {
      if (totalCases === 0) return;
      setExporting(true);
      setProgress(0);
      toast?.(`Exporting ${totalCases.toLocaleString()} records…`, 'info', 8000);

      const chunk = 500;
      let offset = 0;
      const allRows: Record<string, unknown>[] = [];

      try {
        while (offset < totalCases) {
          const payload = { ...basePayload, offset, limit: chunk };
          const { data } = await api.post<FilterResponse>('/api/cases/filter', payload);
          const rows = data.cases?.rows ?? [];
          allRows.push(...rows);
          offset += chunk;
          const pct = Math.min(100, Math.round((offset / totalCases) * 100));
          setProgress(pct);
        }

        // Build CSV
        if (allRows.length === 0) {
          toast?.('No data to export.', 'warning');
          return;
        }
        const columns = Object.keys(allRows[0]);
        const dateColumns = new Set(options?.dateColumns ?? []);
        const header = columns.join(',');
        const body = allRows
          .map((row) =>
            columns
              .map((c) => {
                const s2 = formatExportValue(c, row[c], dateColumns);
                return `"${s2.replace(/"/g, '""')}"`;
              })
              .join(','),
          )
          .join('\n');
        const csv = header + '\n' + body;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cases_export_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast?.(
          `Export complete — ${allRows.length.toLocaleString()} records downloaded.`,
          'success',
        );
      } catch (err) {
        toast?.('Export failed. Please try again.', 'error');
        throw err;
      } finally {
        setExporting(false);
        setProgress(0);
      }
    },
    [],
  );

  return { exportCases, exporting, progress };
}

/* ─────────────────────────────────────────────
 * 8. Global contact search
 * ───────────────────────────────────────────── */

export function useGlobalSearch() {
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const search = useCallback((term: string) => {
    clearTimeout(timerRef.current);
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/api/contacts/search', {
          params: { q: term },
        });
        setResults((data.results ?? []) as GlobalSearchResult[]);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const clear = useCallback(() => {
    clearTimeout(timerRef.current);
    setResults([]);
    setSearching(false);
  }, []);

  return { results, searching, search, clear };
}
